/**
 * Locale-tolerant decimal parsing and formatting helpers.
 *
 * Per BR-2 of EB-3642: the Add Billing dialog must accept both ',' (German) and
 * '.' (English) decimal separators regardless of the current locale, canonicalize
 * to a plain JS number for POST, and format for display using the active locale.
 *
 * Grouping separators (e.g. '1.234,56' / '1,234.56') are intentionally NOT
 * supported — billing amounts in this feature are small and supporting grouping
 * introduces locale ambiguity that would need context we do not have here.
 */

/**
 * Parse a user-entered decimal string to a finite number.
 *
 * Accepts both ',' and '.' as decimal separator regardless of locale.
 * Returns NaN on invalid input — callers decide how to handle (form validation).
 * Trims whitespace. Empty string -> NaN. Multiple separators -> NaN.
 */
export function parseLocaleDecimal(input: string): number {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return NaN;
  }
  // Both separators present = ambiguous (could be grouping or decimal) -> reject.
  if (trimmed.includes(',') && trimmed.includes('.')) {
    return NaN;
  }
  const normalized = trimmed.replace(',', '.');
  const result = Number(normalized);
  if (!Number.isFinite(result)) {
    return NaN;
  }
  return result;
}

